import React, { useState } from 'react';
import { 
  Box, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  CircularProgress
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { useClient } from '../../contexts/ClientContext';

/**
 * Client selector dropdown component
 * Shows a dropdown of available clients and allows selecting one
 * The selected client is saved and displayed in the UI
 * 
 * @returns {JSX.Element} The client selector component
 */
const ClientSelector = () => {
  const { 
    clients, 
    selectedClient, 
    loading, 
    error, 
    fetchClients, 
    selectClient 
  } = useClient();
  
  const [open, setOpen] = useState(false);

  // Handle client selection change
  const handleChange = (event) => {
    const clientId = event.target.value;
    if (clientId === 'none') {
      selectClient(null);
    } else {
      selectClient(Number(clientId));
    }
  };

  // Refresh clients list when dropdown is opened
  const handleOpen = () => {
    setOpen(true);
    fetchClients(); // Refresh the list when opened
  };

  return (
    <Box sx={{ 
      minWidth: 200,
      display: 'flex',
      alignItems: 'center',
      mr: 2
    }}>
      {/* When a client is selected, show chip with name */}
      {selectedClient && (
        <Chip
          icon={<BusinessIcon />}
          label={selectedClient.company_name}
          color="primary"
          variant="outlined"
          onClick={() => setOpen(true)}
          onDelete={() => selectClient(null)}
          sx={{ mr: 1 }}
        />
      )}

      {/* Client dropdown */}
      <FormControl 
        size="small" 
        sx={{ 
          minWidth: selectedClient ? 120 : 200,
          display: selectedClient ? 'none' : 'block'
        }}
      >
        <InputLabel id="client-select-label">Client</InputLabel>
        <Select
          labelId="client-select-label"
          id="client-select"
          value={selectedClient?.id || 'none'}
          label="Client"
          onChange={handleChange}
          open={open}
          onOpen={handleOpen}
          onClose={() => setOpen(false)}
          displayEmpty
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300
              }
            }
          }}
        >
          <MenuItem value="none">
            <em>No client selected</em>
          </MenuItem>
          
          {loading ? (
            <MenuItem disabled>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Loading clients...
            </MenuItem>
          ) : (
            clients.map((client) => (
              <MenuItem key={client.id} value={client.id}>
                {client.company_name}
              </MenuItem>
            ))
          )}
          
          {error && !loading && (
            <MenuItem disabled>
              <Typography color="error" variant="caption">
                Error loading clients
              </Typography>
            </MenuItem>
          )}
        </Select>
      </FormControl>
    </Box>
  );
};

export default ClientSelector;